import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import { TextField, Typography, Box, Grid, FormHelperText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'services/localization/useTranslation';
import { getDaysAndMinutesFromSeconds } from 'common/utils/bookings';

interface Props {
	value?: number;
	onChange?: (value: number) => void;
	error?: string;
	setError?: (error?: string) => void;
}

const MinimalBookingDuration = forwardRef((props: Props, ref) => {
	const { value = 86400, onChange, error, setError } = props;
	const { t } = useTranslation();
	const classes = useStyles();

	const initialValue = useMemo(() => getDaysAndMinutesFromSeconds(value), [value]);
	const [days, setDays] = useState<number | undefined>(initialValue.days);
	const [hours, setHours] = useState<number | undefined>(initialValue.hours);

	const getError = (changedNumber: number | undefined, name: string) => {
		if ((days && days < 0) || (hours && hours < 0) || (changedNumber && changedNumber < 0)) {
			return t('common:validation.cannotBeNegative', {
				defaultValue: '{{field}} cannot be negative',
				field: [t('common:times.days'), t('common:times.hours')].join(', '),
			});
		}
		if (
			(hours && hours > 23 && name !== 'hours') ||
			(name === 'hours' && changedNumber && changedNumber > 23)
		) {
			return t('common:validation.cantExceedNumber', {
				defaultValue: "{{field}} can't exceed {{number}}",
				field: t('common:times.hours'),
				number: '23',
			});
		}
		return;
	};

	const validate = (value: string, name: string) => {
		let newNumber: number | undefined = parseInt(value);
		if (isNaN(newNumber)) {
			newNumber = undefined;
		}
		name === 'days' ? setDays(newNumber) : setHours(newNumber);
		const newError = getError(newNumber, name);
		if (newError !== error) {
			setError?.(newError);
		}
	};

	const handleValueChange = useCallback(() => {
		if (!error) {
			const currentValue = (days ?? 0) * 86400 + (hours ?? 0) * 3600;
			onChange?.(currentValue);
		}
	}, [days, hours, error, onChange]);

	return (
		<Box>
			<Grid container spacing={2} alignItems="flex-start">
				<Grid item className={classes.textFieldWrapper}>
					<TextField
						name="days"
						variant="standard"
						placeholder={'0'}
						value={days}
						onBlur={handleValueChange}
						onChange={(e) => validate(e.target.value, e.target.name)}
						margin="normal"
						type="number"
						fullWidth
						className={classes.textField}
						inputProps={{
							min: 0,
						}}
					/>
					<Typography>{t('common:times.days')}</Typography>
				</Grid>
				<Grid item className={classes.textFieldWrapper}>
					<TextField
						name="hours"
						variant="standard"
						placeholder={'0'}
						value={hours}
						onBlur={handleValueChange}
						onChange={(e) => validate(e.target.value, e.target.name)}
						margin="normal"
						type="number"
						fullWidth
						className={classes.textField}
						inputProps={{
							min: 0,
							max: 23,
						}}
					/>
					<Typography>{t('common:times.hours')}</Typography>
				</Grid>
			</Grid>
			{error && (
				<FormHelperText error className={classes.errorText} data-error="true">
					{error}
				</FormHelperText>
			)}
		</Box>
	);
});

const useStyles = makeStyles((theme) => ({
	textFieldWrapper: {
		display: 'flex',
		alignItems: 'baseline',
	},
	textField: {
		marginRight: theme.spacing(1),
		width: 70,
	},
	errorText: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
}));

export default MinimalBookingDuration;
